import axios, {
  AxiosRequestTransformer,
  AxiosResponseTransformer,
  ParamsSerializerOptions
} from 'axios'
import snakecaseKeys from 'snakecase-keys'
import camelcaseKeys from 'camelcase-keys'
import queryString from 'query-string'
import { env } from 'consts'
import { clearAuthTokens, hasActiveSession } from 'services/token/token.model'

const apiInstance = axios.create({
  baseURL: env.API_BASE_URL,
  transformRequest: [
    function (data) {
      return !!data ? snakecaseKeys(data, { deep: true }) : data
    },
    ...(axios.defaults.transformRequest as AxiosRequestTransformer[])
  ],
  transformResponse: [
    ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
    function (data) {
      return !!data ? camelcaseKeys(data, { deep: true }) : data
    }
  ],
  paramsSerializer: {
    ...(axios.defaults.paramsSerializer as ParamsSerializerOptions),
    serialize: (param) => {
      return queryString.stringify(snakecaseKeys(param, { deep: true }))
    }
  }
})

// Endpoints where a 401 is an expected, in-context result (e.g. wrong login
// credentials) rather than a dead session. These must not trigger a logout.
const UNAUTHENTICATED_ENDPOINTS = [
  '/users/token/obtain/',
  '/users/token/verify/',
  '/users/password/reset/'
]

apiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const requestUrl: string = error?.config?.url ?? ''
    const isUnauthenticatedEndpoint = UNAUTHENTICATED_ENDPOINTS.some((endpoint) =>
      requestUrl.includes(endpoint)
    )

    // A 401 on an authenticated request means the session is dead. Clear the
    // tokens once: this disables every query gated on `accessToken` (stopping
    // polling loops) and lets the route guards redirect to sign-in.
    if (status === 401 && !isUnauthenticatedEndpoint && hasActiveSession()) {
      clearAuthTokens()
    }

    return Promise.reject(error)
  }
)

export default apiInstance
